import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import SpotAlunnoSection from "@/components/SpotAlunnoSection";
import SchedaRiassuntiva from "@/components/SchedaRiassuntiva";
import DocumentiAlunnoModal from "@/components/DocumentiAlunnoModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DAY_NAMES_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTH_NAMES = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

// Mappa codice settore Curriculum → etichetta leggibile per le famiglie.
// I → INFANZIA · P → PRIMARIA · M → SECONDARIA · U → USCITO · altri → codice.
function settoreLabel(s) {
  const c = String(s || "").trim().toUpperCase();
  if (!c) return "—";
  if (c === "I") return "INFANZIA";
  if (c === "P" || c === "E" || c === "F") return "PRIMARIA";
  if (c === "M") return "SECONDARIA";
  if (c === "U") return "USCITO";
  return c;
}

export default function AlunniPortalPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [cf, setCf] = useState("");
  const [linking, setLinking] = useState(false);
  const [err, setErr] = useState(null);
  const [disponibilita, setDisponibilita] = useState(null);
  const [loadingDisp, setLoadingDisp] = useState(false);
  const [modalData, setModalData] = useState(null); // { date, dmy, weekday_name }
  const [saving, setSaving] = useState(false);
  const [dieta, setDieta] = useState("");
  const [note, setNote] = useState("");
  const [toast, setToast] = useState(null);
  // Quale gestione il'alunno ha aperto dalla landing
  const [activeGestione, setActiveGestione] = useState(null); // null | 'extra' | 'spot'

  const isAlunno = user && user !== false && user.user_type === "alunno";
  const isStaff = user && user !== false && user.user_type !== "alunno";

  const logoutStaff = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    window.location.href = "/alunni";
  };

  // Carica profilo alunno
  useEffect(() => {
    if (!isAlunno) return;
    setProfileLoading(true);
    api.get("/alunni-portal/me")
      .then(r => setProfile(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Errore caricamento profilo"))
      .finally(() => setProfileLoading(false));
  }, [isAlunno]);

  const linkCf = async () => {
    setLinking(true); setErr(null);
    try {
      await api.post("/alunni-portal/link", { codice_fiscale: cf.trim().toUpperCase() });
      const r = await api.get("/alunni-portal/me");
      setProfile(r.data);
      setToast({ type: "success", msg: "Account collegato al tuo profilo alunno!" });
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore abbinamento");
    } finally { setLinking(false); }
  };

  // Carica disponibilità (30 giorni)
  const caricaDisp = async () => {
    if (!profile?.linked) return;
    setLoadingDisp(true);
    try {
      const r = await api.get("/alunni-portal/mensa/disponibilita");
      setDisponibilita(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento disponibilità");
    } finally { setLoadingDisp(false); }
  };

  useEffect(() => {
    if (profile?.linked && profile.mensa_attiva) caricaDisp();
  }, [profile]);

  const prenota = async () => {
    if (!modalData) return;
    setSaving(true); setErr(null);
    try {
      await api.post("/alunni-portal/mensa/prenota", { data: modalData.date, dieta, note });
      setModalData(null);
      setDieta(""); setNote("");
      setToast({ type: "success", msg: `Prenotazione confermata per ${modalData.dmy}` });
      await caricaDisp();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore prenotazione");
    } finally { setSaving(false); }
  };

  const annulla = async (day) => {
    if (!window.confirm(`Annullare la prenotazione del ${day.dmy}?`)) return;
    try {
      await api.delete(`/alunni-portal/mensa/prenota/${day.prenotazione_id}`);
      setToast({ type: "success", msg: `Prenotazione annullata (${day.dmy})` });
      await caricaDisp();
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore annullamento");
    }
  };

  const logout = async () => {
    try { await api.post("/alunni-portal/logout"); } catch { /* ignore */ }
    window.location.href = "/alunni";
  };

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3500);
      return () => clearTimeout(t);
    }
  }, [toast]);

  // Raggruppa giorni per mese per rendering calendario semplice
  const giorniRaggruppati = useMemo(() => {
    if (!disponibilita?.days) return [];
    const groups = {};
    for (const d of disponibilita.days) {
      const [y, m] = d.date.split("-");
      const key = `${y}-${m}`;
      if (!groups[key]) groups[key] = { year: y, month: parseInt(m, 10), days: [] };
      groups[key].days.push(d);
    }
    return Object.values(groups);
  }, [disponibilita]);

  if (authLoading) return <FullPageLoader msg="Caricamento..." />;

  // Staff (admin/operatore) loggato → mostra avviso con logout, NON il portale alunni
  if (isStaff) {
    return (
      <div style={{
        minHeight: "100vh",
        backgroundImage: "url('/img/scuola-esterno.jpg')",
        backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}>
        <div style={{ background: "rgba(255,255,255,0.96)", backdropFilter: "blur(6px)", padding: 40, borderRadius: 6, maxWidth: 480, width: "100%", textAlign: "center", boxShadow: "0 12px 40px rgba(0,0,0,0.4)" }}>
          <div style={{ fontSize: 56, marginBottom: 8 }}>🚫</div>
          <h1 style={{ margin: "0 0 4px", fontSize: 22, color: "#4a148c" }}>Area riservata agli Alunni</h1>
          <div style={{ fontSize: 13, color: "#666", marginBottom: 16 }}>
            Sei attualmente loggato come <b>{user.email || user.username || "staff"}</b> (utente staff/amministratore).
          </div>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 24, lineHeight: 1.6 }}>
            Il Portale Alunni è accessibile solo con un account Google studente <b>@canossadiaz.it</b>.<br/>
            Per accedervi, esci dal tuo account e accedi con quello dell'alunno (o usa una finestra di navigazione anonima).
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={logoutStaff} data-testid="alunno-staff-logout"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#c62828", color: "white", padding: "10px 18px", fontWeight: 700, fontSize: 13, border: "none", borderRadius: 4, cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.2)" }}>
              🚪 Esci e vai al login alunni
            </button>
            <button onClick={() => navigate("/", { replace: true })} data-testid="alunno-staff-home"
              style={{ background: "white", color: "#666", padding: "10px 18px", fontWeight: 700, fontSize: 13, border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}>
              Torna al Portale Gestioni
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Not logged in → Google login
  if (!user || user === false) {
    return (
      <div style={{
        minHeight: "100vh",
        backgroundImage: "url('/img/scuola-esterno.jpg')",
        backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}>
        <div style={{ background: "rgba(255,255,255,0.96)", backdropFilter: "blur(6px)", padding: 40, borderRadius: 6, maxWidth: 440, width: "100%", textAlign: "center", boxShadow: "0 12px 40px rgba(0,0,0,0.4)" }}>
          <img src="/img/logo-canossiano.png" alt="Istituto Canossiano #canossadiaz"
            style={{ width: "100%", maxWidth: 320, height: "auto", display: "block", margin: "0 auto 18px", borderRadius: 4 }} />
          <h1 style={{ margin: "0 0 4px", fontSize: 22, color: "#4a148c" }}>Portale Alunni</h1>
          <div style={{ fontSize: 13, color: "#666", marginBottom: 6 }}>Istituto Canossiano · Iscrizioni</div>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 28 }}>
            Accedi con il tuo account Google <b>@canossadiaz.it</b>.
          </div>
          <a href={`${BACKEND_URL}/api/auth/google/login?portal=alunni`}
            data-testid="alunno-google-login"
            style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "#ffc107", color: "#000", padding: "12px 24px", fontWeight: 700, fontSize: 14, textDecoration: "none", borderRadius: 4, boxShadow: "0 2px 8px rgba(0,0,0,0.2)" }}>
            <span style={{ background: "white", color: "#4285f4", width: 22, height: 22, borderRadius: 3, display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 13 }}>G</span>
            Accedi con Google
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f3e5f5" }}>
      <div style={{ background: "linear-gradient(90deg,#8e24aa 0%,#1565c0 100%)", color: "white", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {user.photo_url && <img src={user.photo_url} alt="" style={{ width: 34, height: 34, borderRadius: "50%" }} />}
          <div style={{ textAlign: "right", fontSize: 12 }}>
            <div style={{ fontWeight: 700 }}>{user.name || user.email}</div>
            <div style={{ fontSize: 10, opacity: 0.85 }}>{user.email}</div>
          </div>
          <button onClick={logout} data-testid="alunno-logout"
            style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)", color: "white", padding: "5px 12px", borderRadius: 3, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Esci</button>
        </div>
      </div>

      {err && <div style={{ margin: "12px 24px", padding: 12, background: "#fff0f0", border: "1px solid #fcc", color: "#c00", borderRadius: 4 }} data-testid="alunno-err">{err} <button onClick={() => setErr(null)} style={{ marginLeft: 10, background: "none", border: "none", color: "#c00", cursor: "pointer", fontWeight: 700 }}>✕</button></div>}
      {toast && <div style={{ position: "fixed", top: 80, right: 24, background: toast.type === "success" ? "#2e7d32" : "#c62828", color: "white", padding: "10px 18px", borderRadius: 4, zIndex: 1000, boxShadow: "0 4px 20px rgba(0,0,0,0.25)", fontWeight: 700, fontSize: 13 }} data-testid="alunno-toast">{toast.msg}</div>}

      {profileLoading && <FullPageLoader msg="Caricamento profilo..." />}

      {/* Auto-link non possibile: l'email Google non è collegata a un alunno → segreteria */}
      {profile && !profile.linked && profile.must_contact_segreteria && (
        <div data-testid="alunno-portale-segreteria"
          style={{ maxWidth: 620, margin: "40px auto", background: "linear-gradient(135deg,#ffebee,#ffcdd2)", padding: 28, borderRadius: 6, border: "3px solid #b71c1c", boxShadow: "0 6px 20px rgba(183,28,28,0.2)" }}>
          <div style={{ fontSize: 38, textAlign: "center", marginBottom: 6 }}>⚠</div>
          <h2 style={{ margin: "0 0 12px", color: "#b71c1c", textAlign: "center", fontSize: 18 }}>
            Account non collegato
          </h2>
          <div style={{ fontSize: 13, color: "#7f1d1d", lineHeight: 1.6, textAlign: "center", fontWeight: 600 }}>
            Il tuo account Google <b>{user.email}</b> non risulta abbinato a nessun alunno.
            <br/><br/>
            Prego rivolgersi <b>URGENTEMENTE</b> alla segreteria della scuola al numero{" "}
            <a href="tel:+390302886511" style={{ color: "#b71c1c", fontWeight: 800, textDecoration: "underline", whiteSpace: "nowrap" }}>030 2886511</a>.
            <br/>Il personale della Segreteria vi aiuterà ad attivare l'account.
          </div>
        </div>
      )}

      {/* Non ancora abbinato e NON deve contattare segreteria → chiedi CF (fallback storico) */}
      {profile && !profile.linked && !profile.must_contact_segreteria && (
        <div style={{ maxWidth: 560, margin: "30px auto", background: "white", padding: 28, borderRadius: 6, boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
          <h2 style={{ margin: "0 0 6px", color: "#4a148c", fontSize: 18 }}>🔗 Prova ad attivare il profilo con il codice fiscale dell&apos;alunno</h2>
          <div style={{ fontSize: 13, color: "#555", marginBottom: 16 }}>
            Per attivarti, inserisci il tuo <b>codice fiscale dell&apos;alunno</b>.<br/>
            Dovrebbe avvenire il collegamento col portale iscrizioni.<br/>
            Da quel momento sarai attivo nel portale iscrizioni.<br/>
            <span style={{ color: "#c62828", fontWeight: 700 }}>In caso negativo contattare la segreteria con urgenza</span>.
          </div>
          <input type="text" value={cf} onChange={e => setCf(e.target.value.toUpperCase())} placeholder="es. RSSMRA10A01H501Z"
            data-testid="alunno-cf-input"
            maxLength={16}
            style={{ width: "100%", padding: 10, fontSize: 14, fontFamily: "Consolas, monospace", letterSpacing: 1, border: "2px solid #8e24aa", borderRadius: 4, marginBottom: 12, textTransform: "uppercase" }} />
          <button onClick={linkCf} disabled={linking || cf.length !== 16}
            data-testid="alunno-link-btn"
            style={{ width: "100%", padding: "10px 18px", background: cf.length === 16 ? "#8e24aa" : "#bbb", color: "white", border: "none", borderRadius: 4, cursor: cf.length === 16 ? "pointer" : "not-allowed", fontSize: 14, fontWeight: 700 }}>
            {linking ? "Collegamento..." : "Collega e continua"}
          </button>
          {err && /segreteria/i.test(err) && (
            <div data-testid="alunno-segreteria-warn"
              style={{ marginTop: 14, padding: "12px 14px", background: "linear-gradient(135deg,#ffebee,#ffcdd2)", border: "2px solid #c62828", borderRadius: 4, fontSize: 12, lineHeight: 1.55, color: "#7f1d1d", fontWeight: 600 }}>
              <div style={{ fontWeight: 800, color: "#b71c1c", marginBottom: 4, fontSize: 13 }}>⚠ Attenzione</div>
              Prego rivolgersi <b>URGENTEMENTE</b> alla segreteria della scuola al numero{" "}
              <a href="tel:+390302886511" style={{ color: "#b71c1c", fontWeight: 800, textDecoration: "underline" }}>030 2886511</a>.
              <br/>Il personale della Segreteria vi aiuterà ad attivare l'account.
            </div>
          )}
        </div>
      )}

      {/* Abbinato ma mensa NON attiva: la mostra solo se l'utente ha NESSUNA sezione disponibile */}
      {false && profile?.linked && !profile.mensa_attiva && (
        <div style={{ maxWidth: 560, margin: "30px auto", background: "white", padding: 28, borderRadius: 6, textAlign: "center" }}>
          <div style={{ fontSize: 42, marginBottom: 12 }}>🚫</div>
          <h2 style={{ margin: "0 0 10px", color: "#c62828" }}>Servizio non attivo</h2>
          <div style={{ fontSize: 14, color: "#555", lineHeight: 1.6 }}>
            Ciao <b>{profile.alunno.nome} {profile.alunno.cognome}</b>,<br/>
            il servizio di prenotazione mensa non è disponibile per la tua classe <b>{profile.alunno.classe || "—"}</b>
            {profile.alunno.settore && <> (settore {settoreLabel(profile.alunno.settore)})</>}.<br/>
            Per informazioni contatta la segreteria.
          </div>
        </div>
      )}

      {/* Abbinato → mostra landing gestioni attive + viste */}
      {profile?.linked && (
        <div style={{ maxWidth: 1000, margin: "0 auto", padding: "20px 24px" }}>
          <div style={{ background: "white", padding: 18, borderRadius: 6, marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: "#888", fontWeight: 700, marginBottom: 4 }}>ALUNNO</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#4a148c" }} data-testid="alunno-header-nome">{profile.alunno.nome} {profile.alunno.cognome}</div>
            {(() => {
              const curr = profile.curriculum || {};
              const annoCurr = curr.anno;
              const annoLbl = annoCurr ? `${annoCurr}/${(annoCurr + 1).toString().slice(2)}` : "—";
              const label = curr.label || "—";
              return (
                <div style={{ fontSize: 12, color: "#555" }} data-testid="alunno-header-curriculum">
                  ANNO <b>{annoLbl}</b> · SETTORE CLASSE <b>{label}</b>
                </div>
              );
            })()}
            {/* Anagrafica completa (feb 2026) */}
            {(() => {
              const a = profile.alunno || {};
              const cap_cit_prov = [a.res_cap, a.res_luogo, a.res_prov ? `(${a.res_prov})` : ""]
                .filter(Boolean).join(" ").trim();
              const g = profile.genitori || [];
              return (
                <div data-testid="alunno-header-anagrafica"
                  style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #eee",
                           display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))",
                           gap: "3px 18px", fontSize: 11.5, lineHeight: 1.55, color: "#333" }}>
                  <div><b>Nato a:</b> {a.nato_luogo || "—"}{a.nato_prov ? ` (${a.nato_prov})` : ""}</div>
                  <div><b>Nato il:</b> {a.nato_il || "—"}</div>
                  <div style={{ gridColumn: "1 / span 2" }}><b>Indirizzo:</b> {a.res_via || "—"}</div>
                  <div style={{ gridColumn: "1 / span 2" }}><b>CAP Città Prov.:</b> {cap_cit_prov || "—"}</div>
                  <div style={{ gridColumn: "1 / span 2", fontFamily: "Consolas, monospace" }}><b>Codice Fiscale:</b> {a.cf || "—"}</div>
                  <div data-testid="alunno-header-genitore1" style={{ gridColumn: "1 / span 2", borderTop: "1px dashed #ddd", marginTop: 4, paddingTop: 4 }}>
                    <b>Genitore 1:</b> {g[0] ? `${g[0].cognome} ${g[0].nome}`.trim() : "—"}
                    {g[0]?.cf && <span style={{ marginLeft: 6, fontFamily: "Consolas, monospace", color: "#4a148c" }}>· CF {g[0].cf}</span>}
                  </div>
                  <div data-testid="alunno-header-genitore2" style={{ gridColumn: "1 / span 2" }}>
                    <b>Genitore 2:</b> {g[1] ? `${g[1].cognome} ${g[1].nome}`.trim() : "—"}
                    {g[1]?.cf && <span style={{ marginLeft: 6, fontFamily: "Consolas, monospace", color: "#4a148c" }}>· CF {g[1].cf}</span>}
                  </div>
                </div>
              );
            })()}
          </div>
          {profile.portale_scheda?.enabled && profile.linked && (
            <div data-testid="portale-scheda-section" style={{ margin: "8px 0 20px" }}>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: "#1e3a8a", margin: "0 0 12px" }}>🗂 La mia scheda</h3>
              <SchedaRiassuntiva />
            </div>
          )}
          {(() => {
            const extraOpen = profile.portale_iscrizioni && profile.portale_iscrizioni.is_open_now !== false;
            const spotOpen = profile.portale_spot && profile.portale_spot.is_open_now === true;
            const docsEnabled = profile.portale_documenti?.enabled === true;
            const anyOpen = extraOpen || spotOpen || docsEnabled;
            if (!anyOpen) {
              // Niente è aperto: messaggio dettagliato con date apertura
              const annoIscr = profile.portale_iscrizioni?.anno_iscrizioni
                || profile.portale_spot?.anno_iscrizioni
                || profile.alunno?.anno;
              const annoLbl = annoIscr ? `${annoIscr}/${(annoIscr + 1).toString().slice(2)}` : "—";
              const fmtData = (iso) => {
                if (!iso) return "—";
                try {
                  return new Date(iso).toLocaleString("it-IT", {
                    timeZone: "Europe/Rome",
                    day: "2-digit", month: "long", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  });
                } catch { return iso; }
              };
              return (
                <div data-testid="alunno-portale-chiuso"
                  style={{ background: "linear-gradient(135deg,#fff3e0,#ffe0b2)", border: "2px solid #ef6c00", borderRadius: 6, padding: "26px 22px", boxShadow: "0 4px 14px rgba(239,108,0,0.18)" }}>
                  <div style={{ marginBottom: 18, textAlign: "center" }}>
                    <img src="/img/logo-canossiano.png" alt="Istituto Canossiano"
                      style={{ height: 140, width: "auto", display: "inline-block", background: "white", padding: 12, borderRadius: 10, boxShadow: "0 4px 14px rgba(0,0,0,0.12)" }} />
                  </div>
                  <div style={{ background: "linear-gradient(135deg,#0d47a1,#1565c0)", color: "white", padding: "16px 22px", borderRadius: 6, marginBottom: 16, textAlign: "center", boxShadow: "0 2px 8px rgba(13,71,161,0.25)" }}>
                    <h2 style={{ color: "white", fontSize: 26, margin: 0, fontWeight: 800, letterSpacing: 0.5 }}>
                      ✓ Accesso eseguito regolarmente
                    </h2>
                  </div>
                  <div style={{ fontSize: 13, color: "#37474f", lineHeight: 1.7, background: "white", padding: "12px 16px", borderRadius: 4, marginBottom: 14 }}>
                    Sei loggato per le iscrizioni dell'anno scolastico <b>{annoLbl}</b>.<br />
                    Alunno: <b>{profile.alunno.nome} {profile.alunno.cognome}</b><br />
                    Classe: <b>{profile.curriculum?.classe_num ?? "—"}</b> · Settore: <b>{settoreLabel(profile.curriculum?.settore)}</b>
                  </div>
                  <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
                    <div style={{ padding: "12px 14px", background: "white", border: "1px solid #ede7f6", borderLeft: "4px solid #5e35b1", borderRadius: 4 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: "#4527a0", marginBottom: 6 }}>📚 Iscrizioni Attività Extra</div>
                      {profile.portale_iscrizioni?.stato === "programmato" && profile.portale_iscrizioni?.open_at ? (
                        <div style={{ fontSize: 12, color: "#37474f" }}>
                          Aprirà il <b>{fmtData(profile.portale_iscrizioni.open_at)}</b><br />
                          <span style={{ fontSize: 10, color: "#888" }}>(ora italiana)</span>
                        </div>
                      ) : (
                        <div style={{ fontSize: 11, color: "#888", fontStyle: "italic" }}>Chiuso</div>
                      )}
                    </div>
                    <div style={{ padding: "12px 14px", background: "white", border: "1px solid #e0f2f1", borderLeft: "4px solid #00695c", borderRadius: 4 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: "#004d40", marginBottom: 6 }}>🍽️ Iscrizioni Mensa SPOT</div>
                      {profile.portale_spot?.stato === "programmato" && profile.portale_spot?.open_at ? (
                        <div style={{ fontSize: 12, color: "#37474f" }}>
                          Aprirà il <b>{fmtData(profile.portale_spot.open_at)}</b><br />
                          <span style={{ fontSize: 10, color: "#888" }}>(ora italiana)</span>
                        </div>
                      ) : (
                        <div style={{ fontSize: 11, color: "#888", fontStyle: "italic" }}>Chiuso</div>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: "#666", marginTop: 14, textAlign: "center", fontStyle: "italic" }}>
                    Torna qui non appena una gestione sarà attiva. Riceverai accesso automaticamente.
                  </div>
                </div>
              );
            }
            if (activeGestione === "extra" && extraOpen) {
              return (
                <>
                  <button onClick={() => setActiveGestione(null)} data-testid="alunno-back-landing"
                    style={{ marginBottom: 12, padding: "7px 14px", background: "#455a64", color: "white", border: "none", borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    ← Torna alle gestioni attive
                  </button>
                  <ExtraSection setToast={setToast} setErr={setErr} alunno={profile.alunno} />
                </>
              );
            }
            if (activeGestione === "spot" && spotOpen) {
              return (
                <>
                  <button onClick={() => setActiveGestione(null)} data-testid="alunno-back-landing"
                    style={{ marginBottom: 12, padding: "7px 14px", background: "#455a64", color: "white", border: "none", borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    ← Torna alle gestioni attive
                  </button>
                  <div data-testid="alunno-spot-section">
                    <SpotAlunnoSection setToast={setToast} />
                  </div>
                </>
              );
            }
            if (activeGestione === "docs" && docsEnabled) {
              return (
                <>
                  <button onClick={() => setActiveGestione(null)} data-testid="alunno-back-landing"
                    style={{ marginBottom: 12, padding: "7px 14px", background: "#455a64", color: "white", border: "none", borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    ← Torna alle gestioni attive
                  </button>
                  <DocumentiAlunnoModal alunno={{ Cod: profile.alunno.cod, Cog: profile.alunno.cognome, Nom: profile.alunno.nome, classe: profile.alunno.classe, settore: profile.alunno.settore }}
                    close={() => setActiveGestione(null)} />
                </>
              );
            }
            // Landing: card delle gestioni aperte
            return (
              <div data-testid="alunno-landing-gestioni" style={{ display: "grid", gap: 18, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
                {extraOpen && (
                  <button onClick={() => setActiveGestione("extra")} data-testid="alunno-card-extra"
                    style={{ background: "linear-gradient(135deg,#ede7f6,#d1c4e9)", border: "2px solid #5e35b1", borderRadius: 8, padding: "30px 22px", cursor: "pointer", textAlign: "left", boxShadow: "0 4px 14px rgba(94,53,177,0.18)", transition: "transform 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                    onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                    <div style={{ fontSize: 42, marginBottom: 6 }}>📚</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#4527a0", marginBottom: 6 }}>Iscrizioni Attività Extra</div>
                    <div style={{ fontSize: 12, color: "#311b92", lineHeight: 1.5 }}>
                      Visualizza i corsi extracurriculari disponibili per la tua classe e invia la richiesta di iscrizione.
                    </div>
                    <div style={{ marginTop: 14, fontSize: 12, fontWeight: 700, color: "#4527a0" }}>Apri →</div>
                  </button>
                )}
                {spotOpen && (
                  <button onClick={() => setActiveGestione("spot")} data-testid="alunno-card-spot"
                    style={{ background: "linear-gradient(135deg,#e0f2f1,#b2dfdb)", border: "2px solid #00695c", borderRadius: 8, padding: "30px 22px", cursor: "pointer", textAlign: "left", boxShadow: "0 4px 14px rgba(0,105,92,0.18)", transition: "transform 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                    onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                    <div style={{ fontSize: 42, marginBottom: 6 }}>🍽️</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#004d40", marginBottom: 6 }}>Richieste Mensa Spot</div>
                    <div style={{ fontSize: 12, color: "#004d40", lineHeight: 1.5 }}>
                      Invia richieste per la mensa SPOT in specifici giorni dell'anno scolastico.
                    </div>
                    <div style={{ marginTop: 14, fontSize: 12, fontWeight: 700, color: "#004d40" }}>Apri →</div>
                  </button>
                )}
                {docsEnabled && (
                  <button onClick={() => setActiveGestione("docs")} data-testid="alunno-card-docs"
                    style={{ background: "linear-gradient(135deg,#f3e5f5,#e1bee7)", border: "2px solid #6a1b9a", borderRadius: 8, padding: "30px 22px", cursor: "pointer", textAlign: "left", boxShadow: "0 4px 14px rgba(106,27,154,0.18)", transition: "transform 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                    onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                    <div style={{ fontSize: 42, marginBottom: 6 }}>📁</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#4a148c", marginBottom: 6 }}>I miei documenti</div>
                    <div style={{ fontSize: 12, color: "#4a148c", lineHeight: 1.5 }}>
                      Visualizza e carica i tuoi documenti (iscrizione, certificati, ecc.) richiesti dalla scuola.
                    </div>
                    <div style={{ marginTop: 14, fontSize: 12, fontWeight: 700, color: "#4a148c" }}>Apri →</div>
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Modal prenotazione */}
      {modalData && (
        <div onClick={() => setModalData(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
          <div onClick={e => e.stopPropagation()} data-testid="prenota-modal"
            style={{ background: "white", width: "100%", maxWidth: 460, borderRadius: 6, overflow: "hidden" }}>
            <div style={{ background: "#2e7d32", color: "white", padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>🍽️ Prenota mensa</div>
              <button onClick={() => setModalData(null)} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: 14, marginBottom: 14, color: "#555" }}>
                Confermi la prenotazione mensa per <b style={{ color: "#2e7d32" }}>{modalData.weekday_name} {modalData.dmy}</b>?
              </div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>DIETA / INTOLLERANZA (opzionale)</label>
              <input type="text" value={dieta} onChange={e => setDieta(e.target.value)} maxLength={100}
                placeholder="es. No maiale, Vegetariano, ..."
                data-testid="prenota-dieta"
                style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 3, fontSize: 12, marginBottom: 12 }} />
              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>NOTE (opzionale)</label>
              <textarea value={note} onChange={e => setNote(e.target.value)} maxLength={500} rows={2}
                data-testid="prenota-note"
                style={{ width: "100%", padding: 8, border: "1px solid #ccc", borderRadius: 3, fontSize: 12, resize: "vertical" }} />
            </div>
            <div style={{ padding: "12px 20px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setModalData(null)} style={{ padding: "8px 16px", background: "white", color: "#666", border: "1px solid #ccc", borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
              <button onClick={prenota} disabled={saving} data-testid="prenota-conferma"
                style={{ padding: "8px 20px", background: "#2e7d32", color: "white", border: "none", borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer" }}>
                {saving ? "Prenotazione..." : "✓ Conferma"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DayCard({ day, onPrenota, onAnnulla }) {
  const [y, m, d] = day.date.split("-");
  if (day.prenotata) {
    return (
      <div data-testid={`day-${day.date}`} style={{ padding: 10, background: "#c8e6c9", border: "2px solid #2e7d32", borderRadius: 4, textAlign: "center" }}>
        <div style={{ fontSize: 10, color: "#2e7d32", fontWeight: 700 }}>{DAY_NAMES_SHORT[day.weekday]}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#2e7d32", lineHeight: 1 }}>{d}</div>
        <div style={{ fontSize: 9, color: "#2e7d32" }}>{MONTH_NAMES[parseInt(m, 10) - 1].slice(0, 3)}</div>
        <div style={{ fontSize: 10, color: "#2e7d32", fontWeight: 700, marginTop: 4 }}>✓ PRENOTATA</div>
        <button onClick={onAnnulla} data-testid={`btn-annulla-${day.date}`}
          style={{ marginTop: 5, padding: "3px 8px", background: "white", color: "#c62828", border: "1px solid #c62828", borderRadius: 2, fontSize: 9, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
      </div>
    );
  }
  if (day.bookable) {
    return (
      <button onClick={onPrenota} data-testid={`btn-prenota-${day.date}`}
        style={{ padding: 10, background: "white", border: "2px solid #8e24aa", borderRadius: 4, textAlign: "center", cursor: "pointer", transition: "all 0.15s" }}
        onMouseEnter={e => e.currentTarget.style.background = "#f3e5f5"}
        onMouseLeave={e => e.currentTarget.style.background = "white"}>
        <div style={{ fontSize: 10, color: "#8e24aa", fontWeight: 700 }}>{DAY_NAMES_SHORT[day.weekday]}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#4a148c", lineHeight: 1 }}>{d}</div>
        <div style={{ fontSize: 9, color: "#666" }}>{MONTH_NAMES[parseInt(m, 10) - 1].slice(0, 3)}</div>
        <div style={{ fontSize: 10, color: "#8e24aa", fontWeight: 700, marginTop: 4 }}>+ PRENOTA</div>
      </button>
    );
  }
  return (
    <div data-testid={`day-${day.date}`} style={{ padding: 10, background: "#f5f5f5", border: "1px solid #ddd", borderRadius: 4, textAlign: "center", opacity: 0.55 }}>
      <div style={{ fontSize: 10, color: "#999", fontWeight: 700 }}>{DAY_NAMES_SHORT[day.weekday]}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#999", lineHeight: 1 }}>{d}</div>
      <div style={{ fontSize: 9, color: "#999" }}>{MONTH_NAMES[parseInt(m, 10) - 1].slice(0, 3)}</div>
      <div style={{ fontSize: 9, color: "#999", marginTop: 4, fontStyle: "italic" }}>{day.reason || "Non disp."}</div>
    </div>
  );
}

function FullPageLoader({ msg }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#4a148c", fontSize: 14, fontWeight: 700 }}>{msg}</div>
  );
}

function ExtraSection({ setToast, setErr, alunno }) {
  const [items, setItems] = useState([]);
  const [extraMeta, setExtraMeta] = useState(null);
  const [mie, setMie] = useState([]);
  // 🆕 Filtro anno per "Le mie iscrizioni": default = anno corrente.
  // Sempre nascoste iscrizioni più vecchie di 2 anni rispetto all'anno corrente.
  const [filtroAnnoMie, setFiltroAnnoMie] = useState("current"); // "current" | "all" (entro 2 anni)
  const [loading, setLoading] = useState(false);
  const [confirmCorso, setConfirmCorso] = useState(null);
  const [confirmRead, setConfirmRead] = useState(false);
  // Volontà alunno di usufruire della mensa contestualmente all'extra.
  // Default true per tutti i corsi tranne Prescuola (codice PPRESC o crea_mensa=false).
  const [usaMensa, setUsaMensa] = useState(true);
  // Dopo l'invio: mostriamo una schermata di successo dentro il modal con
  // l'opzione "Iscrivi ad un'altra attività" senza dover uscire e rientrare.
  const [iscrSuccess, setIscrSuccess] = useState(null); // {corso_desc, codice, kind: "new"|"already"}
  // Errore localizzato del modal (es. 409 sovrapposizione giorni): va mostrato
  // DENTRO il modal perché altrimenti rimarrebbe nascosto dietro l'overlay
  // dando l'impressione che la procedura sia bloccata.
  const [modalErr, setModalErr] = useState(null);
  const [tariffaScelta, setTariffaScelta] = useState(null);
  const [giorniScelti, setGiorniScelti] = useState([]);
  const [rataScelta, setRataScelta] = useState("");
  const [saving, setSaving] = useState(false);

  const DAY_NAMES = ["LUN", "MAR", "MER", "GIO", "VEN"];

  const carica = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/alunni-portal/extra/corsi-disponibili"),
        api.get("/alunni-portal/extra/mie-iscrizioni"),
      ]);
      setItems(r1.data?.items || []);
      setExtraMeta({
        ciclo: r1.data?.ciclo || "",
        curriculum_settore: r1.data?.curriculum_settore || "",
        classe_num: r1.data?.classe_num ?? null,
        non_eleggibile: !!r1.data?.non_eleggibile,
        messaggio: r1.data?.messaggio || "",
        anno: r1.data?.anno,
        scadenza_ore: r1.data?.scadenza_ore || 24,
      });
      setMie(r2.data?.items || []);
    } catch (e) {
      setErr && setErr(e.response?.data?.detail || "Errore caricamento attività extra");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { carica(); /* eslint-disable-next-line */ }, []);

  const iscrivi = async () => {
    if (!confirmCorso) return;
    setModalErr(null);
    const hasMultipleTariffe = (confirmCorso.tariffe || []).length > 0;
    if (hasMultipleTariffe && !tariffaScelta) {
      setModalErr("Scegli una tariffa prima di confermare");
      return;
    }
    const nrGg = tariffaScelta?.nr_giorni || 1;
    const giorniDisponibili = confirmCorso.giorni_ammessi || [];
    const forzaTutti = !!confirmCorso.tutti_giorni_obbligatori || confirmCorso.crea_mensa === false;
    // Salta la validazione del numero giorni quando il corso impone tutti
    // i giorni ammessi (es. Prescuola): in quel caso `nr_giorni` della tariffa
    // è solo un indicatore di prezzo, non vincola la selezione.
    if (!forzaTutti && giorniDisponibili.length > nrGg && giorniScelti.length !== nrGg) {
      setModalErr(`Scegli esattamente ${nrGg} giorn${nrGg === 1 ? "o" : "i"} fra quelli ammessi`);
      return;
    }
    setSaving(true);
    try {
      const body = {
        corso_codice: confirmCorso.codice,
        rata_scelta: rataScelta,
        note: "",
        usa_mensa: !!usaMensa,
        // Inviamo gli indici NUMERICI dei giorni (0=Lun, 1=Mar, ..., 4=Ven)
        // — formato richiesto dal backend per il check sovrapposizioni e per
        // popolare la matrice mensa. (Prima inviavamo i nomi "MAR/GIO" e il
        // backend andava in fallback su corso.giorni_ammessi, producendo
        // false-positive di sovrapposizione.)
        giorni_scelti: giorniScelti.sort((a, b) => a - b).join(","),
      };
      if (tariffaScelta) {
        body.tariffa_etichetta = tariffaScelta.etichetta;
        body.tariffa_nr_giorni = tariffaScelta.nr_giorni;
        body.tariffa_importo = tariffaScelta.importo;
      }
      const r = await api.post("/alunni-portal/extra/iscrivimi", body);
      const kind = r.data?.already_exists ? "already" : "new";
      if (kind === "already") {
        setToast && setToast({ type: "info", msg: `Sei già iscritto a "${confirmCorso.descrizione}"` });
      }
      // Aggiorna lista corsi in background e mostra schermata successo nel modal
      setIscrSuccess({ corso_desc: confirmCorso.descrizione, codice: confirmCorso.codice, kind });
      setConfirmRead(false);
      setTariffaScelta(null); setGiorniScelti([]);
      setRataScelta("");
      carica();
    } catch (e) {
      // Mostriamo l'errore DENTRO il modal (es. 409 sovrapposizione giorni)
      // così l'utente vede subito perché la richiesta è stata rifiutata
      // (prima rimaneva nascosto dietro l'overlay).
      const msg = e.response?.data?.detail || "Errore iscrizione";
      setModalErr(msg);
    } finally { setSaving(false); }
  };

  const apriIscrizione = (c) => {
    const t = (c.tariffe || []).length === 1 ? c.tariffe[0] : null;
    setTariffaScelta(t);
    // Se il corso impone tutti i giorni (es. Prescuola), pre-seleziona tutto.
    // Inoltre i corsi con `crea_mensa=false` (tipicamente Prescuola) seguono
    // la stessa regola di default anche se il flag non è stato impostato.
    const forzaTutti = !!c.tutti_giorni_obbligatori || c.crea_mensa === false;
    const disponibili = c.giorni_ammessi || [];
    const nrGg = t?.nr_giorni || 0;
    if (forzaTutti || (nrGg > 0 && disponibili.length <= nrGg)) {
      setGiorniScelti([...disponibili]);
    } else {
      setGiorniScelti([]);
    }
    setRataScelta("");
    setConfirmRead(false);
    // Default "intendo usufruire della mensa":
    // spuntato per tutti i corsi TRANNE Prescuola (codice PPRESC o crea_mensa=false).
    const isPrescuola = (c.codice || "").toUpperCase() === "PPRESC" || c.crea_mensa === false;
    setUsaMensa(!isPrescuola);
    setIscrSuccess(null);
    setModalErr(null);
    setConfirmCorso(c);
  };

  const toggleGiorno = (wd) => {
    const max = tariffaScelta?.nr_giorni || 1;
    setGiorniScelti(prev => {
      if (prev.includes(wd)) return prev.filter(x => x !== wd);
      if (prev.length >= max) return [...prev.slice(1), wd]; // FIFO: drop oldest
      return [...prev, wd];
    });
  };

  const annullaMia = async (id, descr) => {
    if (!window.confirm(`Annullare la richiesta di iscrizione a "${descr}"?`)) return;
    try {
      await api.delete(`/alunni-portal/extra/iscrizione/${id}`);
      setToast && setToast({ type: "success", msg: "Richiesta annullata" });
      carica();
    } catch (e) {
      setErr && setErr(e.response?.data?.detail || "Errore annullamento");
    }
  };

  const STATO_LABEL = {
    pending: { txt: "⏳ In attesa di conferma", color: "#ef6c00", bg: "#fff3e0" },
    ricevuta: { txt: "📥 Ricevuta · in verifica", color: "#ef6c00", bg: "#fff3e0" },
    confirmed: { txt: "⏳ In attesa pagamento", color: "#1b5e20", bg: "#c8e6c9" },
    in_attesa_pagamento: { txt: "💳 In attesa pagamento", color: "#4a148c", bg: "#f3e5f5" },
    accettata_mail_inviata: { txt: "💳 In attesa pagamento", color: "#4a148c", bg: "#f3e5f5" },
    confermata_genitore: { txt: "✓ Confermata (in attesa pag.)", color: "#004d40", bg: "#b2dfdb" },
    accettata: { txt: "✅ Accettata", color: "#1b5e20", bg: "#a5d6a7" },
    rejected: { txt: "✕ Iscrizione rifiutata", color: "#b71c1c", bg: "#ffcdd2" },
    rifiutata: { txt: "✕ Rifiutata", color: "#b71c1c", bg: "#ffcdd2" },
    waiting: { txt: "📋 In lista d'attesa", color: "#6a1b9a", bg: "#f3e5f5" },
    scaduta: { txt: "⏰ Iscrizione scaduta", color: "#5d4037", bg: "#d7ccc8" },
    pagata_da_verificare: { txt: "💳 Pagata · in verifica", color: "#0d47a1", bg: "#e3f2fd" },
    pagata: { txt: "💳 Pagata e verificata", color: "#1b5e20", bg: "#a5d6a7" },
    chiusa: { txt: "🔒 Iscrizione chiusa", color: "#ffffff", bg: "#37474f" },
    recesso: { txt: "⊘ Recesso", color: "#ffffff", bg: "#c62828" },
  };

  const uploadContabile = async (iscrizioneId, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { alert("Il file supera 8 MB"); return; }
    const allowed = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) { alert("Formato non ammesso. Solo PDF, JPG, PNG, WebP."); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/alunni-portal/extra/iscrizione/${iscrizioneId}/contabile`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Contabile caricata correttamente. La segreteria verrà avvisata via email.");
      await carica();
    } catch (e) {
      alert(`Errore upload: ${e.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div style={{ marginTop: 24 }} data-testid="alunno-extra-section">
      <div style={{ background: "linear-gradient(90deg,#1565c0,#0d47a1)", color: "white", padding: "12px 18px", borderRadius: "6px 6px 0 0", fontSize: 15, fontWeight: 800, letterSpacing: 0.5 }}>
        🎨 ATTIVITÀ EXTRA PRENOTABILI
      </div>
      <div style={{ background: "white", padding: 16, borderRadius: "0 0 6px 6px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        {loading && <div style={{ textAlign: "center", color: "#888", padding: 20 }}>Caricamento attività…</div>}
        {!loading && items.length === 0 && (
          <div style={{ textAlign: "center", color: "#666", padding: 20, fontStyle: "italic" }} data-testid="alunno-extra-empty">
            {extraMeta?.non_eleggibile ? (
              <>
                {extraMeta.messaggio || "Non disponibili corsi per l'attuale ciclo"}
                {extraMeta?.ciclo && (
                  <div style={{ fontSize: 11, color: "#999", marginTop: 6, fontStyle: "normal" }}>
                    (Curriculum dell&apos;alunno: <b>{extraMeta.ciclo}</b>)
                  </div>
                )}
              </>
            ) : extraMeta?.curriculum_settore ? (
              <>
                Nessuna attività extra disponibile per il tuo curriculum
                {" "}<b>{extraMeta.curriculum_settore}{extraMeta.classe_num ?? ""}</b>
                {extraMeta.anno ? <> nell&apos;anno <b>{extraMeta.anno}/{(extraMeta.anno + 1).toString().slice(-2)}</b></> : null}.
                <div style={{ fontSize: 11, color: "#999", marginTop: 6, fontStyle: "normal" }}>
                  Se ritieni si tratti di un errore, contatta la segreteria.
                </div>
              </>
            ) : (
              <>Nessuna attività extra disponibile.</>
            )}
          </div>
        )}
        {!loading && items.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {items.map(c => {
              const giaIscritto = !!c.mia_iscrizione;
              const stato = c.mia_iscrizione?.stato;
              const lbl = STATO_LABEL[stato];
              const tariffe = c.tariffe || [];
              const rate = c.rate || [];
              const nRate = rate.length;
              const minImp = tariffe.reduce((acc, t) => Math.min(acc, Number(t.importo || 0)), Number.POSITIVE_INFINITY);
              const maxImp = tariffe.reduce((acc, t) => Math.max(acc, Number(t.importo || 0)), 0);
              const importoFmt = (v) => v.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              const prezzoLabel = tariffe.length === 0 ? null
                : (minImp === maxImp ? `€ ${importoFmt(maxImp)}` : `da € ${importoFmt(minImp)} a € ${importoFmt(maxImp)}`);
              const rateLabel = nRate > 0 ? `${nRate} rat${nRate === 1 ? "a" : "e"}` : null;
              const scadenzeLabel = rate.filter(r => r.scadenza).map(r => r.scadenza).join(" · ");
              const classiAmm = c.classi_ammesse || [];
              return (
                <div key={c.codice} data-testid={`alunno-extra-corso-${c.codice}`}
                  style={{ border: "2px solid #1565c0", borderRadius: 6, padding: 12, background: giaIscritto ? "#e8eaf6" : "white" }}>
                  <div style={{ fontSize: 10, fontFamily: "monospace", color: "#1565c0", fontWeight: 700, marginBottom: 3 }}>{c.codice}</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#0d47a1", marginBottom: 6, lineHeight: 1.2 }}>{c.descrizione}</div>
                  {prezzoLabel && (
                    <div style={{ fontSize: 13, color: "#1b5e20", fontWeight: 800, marginBottom: 4 }}
                         title={tariffe.map(t => `${t.etichetta}: € ${importoFmt(Number(t.importo || 0))}`).join("\n")}>
                      {rateLabel ? `${rateLabel} ${prezzoLabel}` : prezzoLabel}
                    </div>
                  )}
                  {scadenzeLabel && (
                    <div style={{ fontSize: 11, color: "#33691e", marginBottom: 4 }}
                         title={rate.map(r => `${r.nome}${r.scadenza ? ` → scadenza ${r.scadenza}` : ""}`).join("\n")}>
                      📅 Scadenze: {scadenzeLabel}
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: "#bf360c", fontWeight: 700, marginBottom: 6 }}>
                    🎓 Classi ammesse: {classiAmm.length === 0 ? "Tutte" : classiAmm.join(", ")}
                  </div>
                  {(c.giorni_ammessi || []).length > 0 && (
                    <div data-testid={`alunno-extra-giorni-${c.codice}`}
                         style={{ display: "flex", gap: 3, marginBottom: 8, flexWrap: "wrap" }}>
                      {DAY_NAMES.map((nm, wd) => {
                        const amm = (c.giorni_ammessi || []).includes(wd);
                        if (!amm) return null;
                        return (
                          <span key={wd}
                            style={{ padding: "2px 8px", background: "#e3f2fd", color: "#0d47a1",
                                     border: "1px solid #90caf9", fontSize: 10, fontWeight: 800,
                                     borderRadius: 10, letterSpacing: 0.3 }}>
                            {nm}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {/* Orario corso — testo semplice sotto la lista giorni (richiesta utente feb-2026) */}
                  {c.orario && (
                    <div data-testid={`alunno-extra-orario-${c.codice}`}
                         style={{ fontSize: 12, color: "#0d47a1", marginBottom: 8,
                                  fontWeight: 700, letterSpacing: 0.3, textAlign: "center" }}>
                      🕐 {c.orario}
                    </div>
                  )}
                  {giaIscritto && lbl && (
                    <div style={{ marginTop: 8, padding: "6px 10px", background: lbl.bg, color: lbl.color, fontSize: 11, fontWeight: 800, borderRadius: 3, textAlign: "center" }}>
                      {lbl.txt}
                      {stato === "pending" && (
                        <button onClick={() => annullaMia(c.mia_iscrizione.id, c.descrizione)} data-testid={`alunno-extra-annulla-${c.codice}`}
                          style={{ display: "block", margin: "6px auto 0", padding: "3px 10px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>Annulla richiesta</button>
                      )}
                    </div>
                  )}
                  {!giaIscritto && (
                    <button onClick={() => apriIscrizione(c)}
                      data-testid={`alunno-extra-iscrivi-${c.codice}`}
                      style={{ width: "100%", padding: "8px 12px", background: "#1565c0", color: "white", border: "none", borderRadius: 3, fontSize: 12, fontWeight: 800, cursor: "pointer", letterSpacing: 0.3 }}>
                      ➕ Iscriviti
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal conferma iscrizione */}
      {confirmCorso && (
        <div onClick={() => setConfirmCorso(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
          <div onClick={e => e.stopPropagation()} data-testid="alunno-extra-modal"
            style={{ background: "white", width: "100%", maxWidth: 460, borderRadius: 6, overflow: "hidden",
                     display: "flex", flexDirection: "column", maxHeight: "92vh" }}>
            <div style={{ background: "#1565c0", color: "white", padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>🎨 Iscrizione attività extra</div>
              <button onClick={() => setConfirmCorso(null)} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: 20, overflowY: "auto", flex: 1 }} data-testid="alunno-extra-modal-body">
              {iscrSuccess ? (
                <div data-testid="alunno-extra-success" style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 48, marginBottom: 10 }}>
                    {iscrSuccess.kind === "already" ? "ℹ️" : "🎉"}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: iscrSuccess.kind === "already" ? "#1565c0" : "#1b5e20", marginBottom: 8 }}>
                    {iscrSuccess.kind === "already" ? "Sei già iscritto" : "Richiesta inviata!"}
                  </div>
                  <div style={{ fontSize: 13, color: "#333", marginBottom: 14 }}>
                    {iscrSuccess.kind === "already"
                      ? <>Risulti già iscritto a <b>{iscrSuccess.corso_desc}</b>.</>
                      : <>La tua iscrizione a <b>{iscrSuccess.corso_desc}</b> è stata inviata alla segreteria. Dopo gli opportuni controlli e la verifica che la tua richiesta rientra nel massimo di adesioni che il corso può accettare, riceverai una mail di conferma con le istruzioni di pagamento, altrimenti verrai messo in lista d&apos;attesa.</>}
                  </div>
                  <div style={{ background: "#e8f5e9", border: "1px solid #81c784", color: "#1b5e20", padding: 12, borderRadius: 4, fontSize: 12, marginBottom: 16, lineHeight: 1.4 }}>
                    💡 Puoi iscrivere lo stesso alunno ad <b>un&apos;altra attività</b> senza uscire dal portale.
                  </div>
                  <div style={{ display: "flex", gap: 10, flexDirection: "column" }}>
                    <button onClick={() => { setIscrSuccess(null); setConfirmCorso(null); }}
                      data-testid="alunno-extra-success-altra"
                      style={{ padding: "12px 16px", background: "#1565c0", color: "white", border: "none", borderRadius: 4, fontSize: 13, fontWeight: 800, cursor: "pointer", letterSpacing: 0.3 }}>
                      ➕ Iscrivi ad un&apos;altra attività
                    </button>
                    <button onClick={() => { setIscrSuccess(null); setConfirmCorso(null); }}
                      data-testid="alunno-extra-success-chiudi"
                      style={{ padding: "10px 16px", background: "white", color: "#666", border: "1px solid #bbb", borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                      ✓ Ho finito · Chiudi
                    </button>
                  </div>
                </div>
              ) : (
                <>
              <div style={{ fontSize: 10, fontFamily: "monospace", color: "#1565c0", fontWeight: 700, marginBottom: 3 }}>{confirmCorso.codice}</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#0d47a1", marginBottom: 10 }}>{confirmCorso.descrizione}</div>

              {(confirmCorso.tariffe || []).length > 0 && (
                <>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>SCEGLI LA TARIFFA *</label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                    {confirmCorso.tariffe.map((t, i) => {
                      const sel = tariffaScelta && tariffaScelta.etichetta === t.etichetta && tariffaScelta.nr_giorni === t.nr_giorni;
                      return (
                        <div key={i} onClick={() => {
                            setTariffaScelta(t);
                            // Re-auto-popola giorni quando si cambia tariffa
                            const forzaTutti = !!confirmCorso.tutti_giorni_obbligatori || confirmCorso.crea_mensa === false;
                            const disponibili = confirmCorso.giorni_ammessi || [];
                            const nrGg = t?.nr_giorni || 0;
                            if (forzaTutti || (nrGg > 0 && disponibili.length <= nrGg)) {
                              setGiorniScelti([...disponibili]);
                            } else {
                              setGiorniScelti([]);
                            }
                          }} data-testid={`alunno-extra-tariffa-${i}`}
                          style={{ padding: 10, border: sel ? "2px solid #1565c0" : "1px solid #ccc", background: sel ? "#e3f2fd" : "white", cursor: "pointer", borderRadius: 3, display: "flex", alignItems: "center", gap: 10 }}>
                          <input type="radio" checked={!!sel} readOnly />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: 12 }}>{t.etichetta}</div>
                            <div style={{ fontSize: 10, color: "#666" }}>{t.nr_giorni} giorno{t.nr_giorni === 1 ? "" : "i"}/settimana</div>
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 800, color: "#1b5e20" }}>€ {Number(t.importo || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Step scelta giorni (compare se la tariffa nr_giorni < giorni_ammessi del corso).
                  Se il corso ha `tutti_giorni_obbligatori` (es. Prescuola): TUTTI i giorni
                  ammessi sono pre-selezionati e bloccati. */}
              {tariffaScelta && (confirmCorso.giorni_ammessi || []).length > 0 && (() => {
                const nrGg = tariffaScelta.nr_giorni || 1;
                const disponibili = confirmCorso.giorni_ammessi || [];
                const forzaTutti = !!confirmCorso.tutti_giorni_obbligatori || confirmCorso.crea_mensa === false;
                const auto = forzaTutti || disponibili.length <= nrGg;
                return (
                  <>
                    <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 3 }}>
                      {forzaTutti ? "GIORNI INCLUSI (tutti, obbligatori)" : auto ? "GIORNI DEL CORSO" : `SCEGLI ${nrGg} GIORN${nrGg === 1 ? "O" : "I"} FRA QUELLI AMMESSI *`}
                    </label>
                    <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
                      {DAY_NAMES.map((nm, wd) => {
                        const amm = disponibili.includes(wd);
                        if (!amm) return null;
                        const isSel = auto ? true : giorniScelti.includes(wd);
                        return (
                          <button key={wd}
                            onClick={() => { if (!auto) toggleGiorno(wd); }}
                            disabled={auto}
                            data-testid={`alunno-extra-day-${wd}`}
                            style={{ flex: 1, padding: "10px 6px",
                              border: `2px solid ${isSel ? "#1565c0" : "#ccc"}`,
                              background: isSel ? "#1565c0" : "white",
                              color: isSel ? "white" : "#666",
                              fontSize: 12, fontWeight: 800, cursor: auto ? "default" : "pointer", letterSpacing: 0.3 }}>
                            {nm}
                          </button>
                        );
                      })}
                    </div>
                    {!auto && (
                      <div style={{ fontSize: 10, color: giorniScelti.length === nrGg ? "#1b5e20" : "#ef6c00", marginBottom: 12, fontWeight: 700 }}>
                        {giorniScelti.length === nrGg ? `✓ ${nrGg} giorni selezionati` : `Hai selezionato ${giorniScelti.length}/${nrGg}`}
                      </div>
                    )}
                  </>
                );
              })()}

              {(confirmCorso.rate || []).length > 0 && (
                <div style={{ marginBottom: 12, padding: 10, background: "#e8f5e9", border: "1px solid #81c784", borderRadius: 3 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#1b5e20", marginBottom: 4 }}>RATE DI PAGAMENTO</div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "#33691e" }}>
                    {confirmCorso.rate.map((r, i) => (
                      <li key={i}>
                        <b>{r.nome}</b>{r.scadenza ? <> · scadenza <b>{r.scadenza}</b></> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div style={{ marginTop: 8, padding: "6px 10px", background: "#c62828", color: "white", fontSize: 12, fontWeight: 800, letterSpacing: 0.5, textAlign: "center", borderRadius: 3 }} data-testid="alunno-extra-importante">
                ⚠️ IMPORTANTE! LEGGERE CON ATTENZIONE
              </div>
              <div style={{ marginTop: 6, padding: 12, background: "#fff8e1", border: "1px solid #ffd54f", borderRadius: 4, fontSize: 11, color: "#5d4037", lineHeight: 1.5 }} data-testid="alunno-extra-avviso-forfettario">
                <b>ℹ️ Si ricorda</b> che, per motivi organizzativi e gestionali, il costo del corso è da pagare <b>interamente (tutte le rate)</b> anche <b>in caso di rinuncia e non partecipazione</b> durante l&apos;anno.
              </div>
              <div style={{ marginTop: 6, padding: 10, background: "#fff3e0", border: "1px solid #ffb74d", color: "#bf360c", fontSize: 11, borderRadius: 3, lineHeight: 1.5 }} data-testid="alunno-extra-info-segreteria">
                ⏳ La tua prenotazione <b>verrà inviata alla segreteria</b>.<br />
                La segreteria eseguirà le dovute verifiche.<br />
                Solo se l&apos;iscrizione sarà <b>in graduatoria entro il numero massimo di alunni accettati</b> per quel corso/attività, vi sarà inviata una <b>mail di conferma</b> (al vostro indirizzo <b>@canossadiaz.it</b>) con riportate le istruzioni di pagamento <b>da farsi entro {extraMeta?.scadenza_ore || 24} ore</b> dalla mail (che è <b>requisito essenziale</b> per la conferma definitiva dell&apos;iscrizione).
              </div>
              <div style={{ marginTop: 6, padding: 10, background: "#e1f5fe", border: "1px solid #4fc3f7", color: "#01579b", fontSize: 11, borderRadius: 3, lineHeight: 1.5 }} data-testid="alunno-extra-info-mensa">
                🍽️ L&apos;iscrizione alle attività extracurriculari prevede la <b>contestuale automatica iscrizione alla mensa</b> del giorno in cui si svolge l&apos;attività (<b>non dovete richiederla separatamente!</b>). Ricordiamo che <b>la mensa è comunque a pagamento</b> e verrà calcolata per i reali giorni di utilizzo.
              </div>
              {/* Spunta volontà mensa — default ON per tutti tranne Prescuola */}
              <label
                data-testid="alunno-extra-usa-mensa-label"
                style={{
                  marginTop: 8, padding: "10px 14px", display: "flex", alignItems: "flex-start", gap: 10,
                  background: usaMensa ? "#e0f2f1" : "#ffebee",
                  border: `2px solid ${usaMensa ? "#00695c" : "#c62828"}`,
                  borderRadius: 4, cursor: "pointer", fontSize: 12.5, fontWeight: 700,
                  color: usaMensa ? "#004d40" : "#b71c1c",
                }}>
                <input type="checkbox" checked={usaMensa}
                  onChange={e => setUsaMensa(e.target.checked)}
                  data-testid="alunno-extra-usa-mensa"
                  style={{ width: 20, height: 20, cursor: "pointer", flexShrink: 0, marginTop: 1 }} />
                <span>
                  {usaMensa
                    ? <>🍽️ <b>Intendo usufruire della mensa</b> nei giorni del corso</>
                    : <>🚫 <b>NON intendo usufruire della mensa</b> — porto pranzo da casa o ritiro l&apos;alunno prima</>}
                  <div style={{ fontWeight: 400, fontSize: 11, marginTop: 3, color: usaMensa ? "#00695c" : "#c62828" }}>
                    Puoi togliere la spunta se non vuoi essere iscritto automaticamente al servizio mensa.
                  </div>
                </span>
              </label>
              {/* Checkbox conferma lettura */}
              <label
                data-testid="alunno-extra-conferma-letto-label"
                style={{
                  marginTop: 12, padding: "12px 14px", display: "flex", alignItems: "flex-start", gap: 10,
                  background: confirmRead ? "#e8f5e9" : "#fff8e1",
                  border: `2px solid ${confirmRead ? "#2e7d32" : "#ff8f00"}`,
                  borderRadius: 4, cursor: "pointer", fontSize: 12.5, fontWeight: 700, color: confirmRead ? "#1b5e20" : "#bf360c",
                }}>
                <input type="checkbox" checked={confirmRead} onChange={e => setConfirmRead(e.target.checked)}
                  data-testid="alunno-extra-conferma-letto"
                  style={{ width: 20, height: 20, cursor: "pointer", flexShrink: 0, marginTop: 1 }} />
                <span>Confermo di avere letto, controllato i dati e accettato tutte le condizioni</span>
              </label>
                </>
              )}
            </div>
            {!iscrSuccess && modalErr && (
              <div data-testid="alunno-extra-modal-err"
                style={{ margin: "0 20px", padding: "10px 12px", background: "#ffebee", border: "1px solid #ef5350", color: "#b71c1c", borderRadius: 3, fontSize: 12, fontWeight: 600, display: "flex", alignItems: "flex-start", gap: 8, flexShrink: 0 }}>
                <span style={{ fontSize: 16 }}>⚠️</span>
                <div style={{ flex: 1 }}>{modalErr}</div>
                <button onClick={() => setModalErr(null)}
                  style={{ background: "transparent", border: "none", color: "#b71c1c", cursor: "pointer", fontWeight: 800, fontSize: 14 }}>✕</button>
              </div>
            )}
            {!iscrSuccess && (
            <div style={{ padding: "12px 20px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0, background: "#fafafa" }}>
              <button onClick={() => { setConfirmCorso(null); setModalErr(null); }} style={{ padding: "8px 16px", background: "white", color: "#666", border: "1px solid #ccc", borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
              <button onClick={iscrivi} disabled={saving || !confirmRead} data-testid="alunno-extra-conferma"
                title={!confirmRead ? "Spunta prima la conferma di lettura" : undefined}
                style={{ padding: "8px 20px", background: (saving || !confirmRead) ? "#bbb" : "#1565c0", color: "white", border: "none", borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: (saving || !confirmRead) ? "not-allowed" : "pointer" }}>
                {saving ? "Invio..." : "✓ Invia richiesta"}
              </button>
            </div>
            )}
          </div>
        </div>
      )}

      {/* Storico mie iscrizioni */}
      {mie.length > 0 && (() => {
        // 🆕 Determina anno corrente (prima da extraMeta, poi dal max in mie)
        const annoCorr = extraMeta?.anno || Math.max(...mie.map(m => Number(m.anno) || 0));
        // Sempre nascondi iscrizioni più vecchie di 2 anni (annoCorr - 2)
        const limitAnno = annoCorr - 2;
        const filtrate = mie
          .filter(m => Number(m.anno) >= limitAnno)
          .filter(m => filtroAnnoMie === "all" ? true : Number(m.anno) === annoCorr);
        const hidden = mie.length - filtrate.length;
        return (
        <div style={{ marginTop: 14, background: "white", padding: 14, borderRadius: 6, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#0d47a1" }}>📋 LE MIE ISCRIZIONI {filtroAnnoMie === "current" ? `· A.S. ${annoCorr}/${(annoCorr + 1).toString().slice(2)}` : "· Ultimi 2 anni"}</div>
            {hidden > 0 && (
              <button onClick={() => setFiltroAnnoMie(filtroAnnoMie === "current" ? "all" : "current")}
                data-testid="alunno-mie-filtro-anno"
                style={{ background: filtroAnnoMie === "current" ? "#1565c0" : "#37474f", color: "white", border: "none", padding: "5px 12px", fontSize: 11, fontWeight: 700, borderRadius: 3, cursor: "pointer" }}>
                {filtroAnnoMie === "current" ? `📂 Mostra ${hidden} iscrizion${hidden === 1 ? "e" : "i"} precedent${hidden === 1 ? "e" : "i"}` : "🔙 Solo anno corrente"}
              </button>
            )}
          </div>
          {filtrate.length === 0 ? (
            <div style={{ padding: 14, textAlign: "center", color: "#888", fontSize: 11, fontStyle: "italic", background: "#fafafa", borderRadius: 3 }}>
              Nessuna iscrizione per l&apos;anno scolastico corrente.
            </div>
          ) : (
          <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#37474f", color: "white" }}>
                <th style={{ textAlign: "left", padding: 6 }}>CORSO</th>
                <th style={{ textAlign: "center", padding: 6 }}>STATO</th>
                <th style={{ textAlign: "center", padding: 6, width: 200 }}>CONTABILE PAGAMENTO</th>
              </tr>
            </thead>
            <tbody>
              {filtrate.map(m => {
                const lbl = STATO_LABEL[m.stato_iscrizione] || STATO_LABEL.pending;
                const canUpload = ["in_attesa_pagamento", "accettata_mail_inviata", "confermata_genitore", "pagata_da_verificare", "pagata"].includes(m.stato_iscrizione);
                return (
                  <tr key={m.id} data-testid={`alunno-extra-mia-${m.id}`} style={{ borderTop: "1px solid #eee" }}>
                    <td style={{ padding: 6 }}>
                      <div style={{ fontWeight: 700 }}>{m.corso_descrizione || m.corso_codice}</div>
                      <div style={{ fontSize: 9, color: "#888", fontFamily: "monospace" }}>
                        {m.corso_codice ? `${m.corso_codice} · ` : ""}A.S. {m.anno}
                        {m.tariffa_etichetta ? ` · ${m.tariffa_etichetta}` : (m.nr_giorni ? ` · ${m.nr_giorni}gg` : "")}
                        {m.giorni_scelti ? ` · ${m.giorni_scelti}` : ""}
                      </div>
                      {m.created_at && (
                        <div style={{ fontSize: 10, color: "#37474f", fontFamily: "monospace", marginTop: 2 }}
                          title={`Data e ora di invio della richiesta: ${m.created_at}`}>
                          🕒 {(() => {
                            try {
                              const d = new Date(m.created_at);
                              return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
                            } catch { return m.created_at; }
                          })()}
                        </div>
                      )}
                      {(m.tariffa_importo || m.importo_rata) ? (
                        <div style={{ fontSize: 10, color: "#1b5e20", fontWeight: 700 }}>€ {Number(m.tariffa_importo || m.importo_rata).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</div>
                      ) : null}
                    </td>
                    <td style={{ textAlign: "center", padding: 6 }}>
                      <span style={{ padding: "2px 8px", background: lbl.bg, color: lbl.color, fontWeight: 800, borderRadius: 2 }}>{lbl.txt}</span>
                    </td>
                    <td style={{ textAlign: "center", padding: 6 }}>
                      {!canUpload ? (
                        <span style={{ fontSize: 10, color: "#bbb", fontStyle: "italic" }}>—</span>
                      ) : (
                        <>
                          {m.has_contabile ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
                              <div style={{ fontSize: 10, color: "#0d47a1", fontFamily: "monospace" }} title={m.contabile_filename}>
                                ✓ {(m.contabile_filename || "caricata").slice(0, 28)}{(m.contabile_filename || "").length > 28 ? "…" : ""}
                              </div>
                              {m.contabile_uploaded_at && (
                                <div style={{ fontSize: 9, color: "#666", fontFamily: "monospace" }}
                                  title={`Caricata il ${m.contabile_uploaded_at}`}>
                                  🕒 {(() => {
                                    try {
                                      const d = new Date(m.contabile_uploaded_at);
                                      return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
                                    } catch { return m.contabile_uploaded_at; }
                                  })()}
                                </div>
                              )}
                              <label style={{ display: "inline-block", background: "#ef6c00", color: "white", padding: "3px 10px", fontSize: 10, fontWeight: 700, borderRadius: 2, cursor: "pointer" }}>
                                🔄 Sostituisci
                                <input type="file" accept="application/pdf,image/*"
                                  onChange={(e) => uploadContabile(m.id, e.target.files?.[0])}
                                  data-testid={`alunno-contabile-upload-${m.id}`}
                                  style={{ display: "none" }} />
                              </label>
                            </div>
                          ) : (
                            <label style={{ display: "inline-block", background: "#1565c0", color: "white", padding: "5px 12px", fontSize: 11, fontWeight: 700, borderRadius: 2, cursor: "pointer" }}>
                              📎 Carica contabile bonifico
                              <input type="file" accept="application/pdf,image/*"
                                onChange={(e) => uploadContabile(m.id, e.target.files?.[0])}
                                data-testid={`alunno-contabile-upload-${m.id}`}
                                style={{ display: "none" }} />
                            </label>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
        );
      })()}

      {/* Nota informativa "Variazioni e ritiri" - sempre visibile (anche senza iscrizioni) */}
      <VariazioniRitiriBox />
    </div>
  );
}

function VariazioniRitiriBox() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div data-testid="alunno-variazioni-box"
           style={{ marginTop: 14, background: "#fff8e1", border: "2px solid #ffb300", borderRadius: 6, padding: "12px 16px", display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 24, lineHeight: 1 }}>ℹ️</div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#bf360c", marginBottom: 4 }}>
            VARIAZIONI E RITIRI DALLE ATTIVITÀ EXTRA
          </div>
          <div style={{ fontSize: 12, color: "#5d4037", lineHeight: 1.5 }}>
            Per eventuali variazioni e ritiri di alunni dalle attività extra curriculari <b>contattare la segreteria</b>.
          </div>
        </div>
        <button onClick={() => setOpen(true)}
                data-testid="alunno-variazioni-btn"
                style={{ padding: "8px 14px", background: "#ef6c00", color: "white", border: "none", fontSize: 11, fontWeight: 800, borderRadius: 3, cursor: "pointer", letterSpacing: 0.3, whiteSpace: "nowrap" }}>
          ℹ️ Leggi condizioni
        </button>
      </div>
      {open && (
        <div onClick={() => setOpen(false)}
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 900,
                      display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()}
               data-testid="alunno-variazioni-popup"
               style={{ background: "white", width: 560, maxWidth: "100%", maxHeight: "85vh", overflow: "auto", borderRadius: 6, boxShadow: "0 10px 30px rgba(0,0,0,0.3)" }}>
            <div style={{ background: "linear-gradient(90deg,#bf360c,#e65100)", color: "white", padding: "14px 18px" }}>
              <div style={{ fontSize: 14, fontWeight: 800 }}>📋 Variazioni e ritiri — Condizioni</div>
            </div>
            <div style={{ padding: 18, fontSize: 13, color: "#333", lineHeight: 1.6 }}>
              <p style={{ marginTop: 0, marginBottom: 4, fontWeight: 700, color: "#bf360c" }}>
                Iscrizione ai laboratori opzionali — condizioni:
              </p>
              <ol style={{ paddingLeft: 22, margin: 0 }}>
                <li style={{ marginBottom: 8 }}>
                  <b>Scadenza delle rate</b>: 1<sup>a</sup> all&apos;iscrizione &mdash; 2<sup>a</sup> entro il <b>31/12/2026</b> (solo per pagamenti quadrimestrali).
                </li>
                <li style={{ marginBottom: 8 }}>
                  Ciascun laboratorio sarà attivato solo al raggiungimento del <b>numero minimo di iscritti</b> stabilito (<b>15</b>).
                </li>
                <li style={{ marginBottom: 8 }}>
                  Gli iscritti ai laboratori opzionali saranno <b>inseriti automaticamente in mensa</b>.
                  Qualora la famiglia non desiderasse usufruire del servizio mensa, dovrà mandare <b>richiesta specifica alla segreteria</b>.
                </li>
                <li style={{ marginBottom: 8 }}>
                  La frequenza deve essere <b>continua</b> e l&apos;<b>assenza giustificata</b> dal genitore.
                </li>
                <li style={{ marginBottom: 8 }}>
                  Una volta scelto il servizio, esso diventa <b>obbligatorio fino al termine dell&apos;anno scolastico</b>, ivi compreso il pagamento anche in caso di <b>ritiro</b>.
                </li>
                <li style={{ marginBottom: 8 }}>
                  Il pagamento di ciascun corso deve essere saldato con le modalità e i termini indicati, <b>solo dopo aver ricevuto conferma dalla segreteria</b>. Il mancato saldo farà perdere la priorità acquisita, pertanto il posto sarà assegnato ad altri attingendo alla <b>lista d&apos;attesa</b>.
                </li>
              </ol>
              <p style={{ marginTop: 14, marginBottom: 0, fontStyle: "italic", color: "#5d4037" }}>
                Dato che i laboratori facoltativi pomeridiani sono attività aggiuntive, potrebbe accadere che il loro calendario subisca <b>modifiche o sospensioni</b> al momento non prevedibili.
              </p>
              <div style={{ marginTop: 16, padding: 10, background: "#fff3e0", border: "1px solid #ffb74d", borderRadius: 3, fontSize: 11, color: "#6d4c00" }}>
                💡 Per ogni operazione di <b>variazione</b> o <b>ritiro</b>, rivolgersi direttamente alla <b>segreteria della scuola</b>.
              </div>
            </div>
            <div style={{ padding: 12, borderTop: "1px solid #eee", textAlign: "right" }}>
              <button onClick={() => setOpen(false)}
                      data-testid="alunno-variazioni-close"
                      style={{ padding: "8px 18px", background: "#37474f", color: "white", border: "none", fontSize: 12, fontWeight: 800, borderRadius: 3, cursor: "pointer" }}>
                ✕ Ho letto, chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
